import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import api from '@/api';
import { ArchiveSidebar } from '@/archive/ArchiveSidebar';
import { nextArchivePage, PAGE_SIZE, PostArchive } from '@/archive/PostArchive';
import { apiLanguage } from '~/i18n';
import { localeToApiLanguage } from '~/utils/languageFormat';

/**
 * The Study Helper News archive, newest first like the student one; only the
 * tutorial archives are reordered.
 *
 * Open to anyone signed in, as in legacy; only the study helper tutorials
 * archive has a role gate.
 */
function StudyHelperNewsArchive(): ReactElement {
  // The interface language, which is part of the query key, so a language
  // switch refetches. Unlike legacy there's no language prefix in the URL.
  const language = localeToApiLanguage(apiLanguage());

  const query = api.listStudyHelperNews.useInfiniteQuery(
    { query: { language, limit: PAGE_SIZE } },
    {
      initialPageParam: { query: { offset: 0 } },
      // Wrapped rather than passed by reference: an annotated `lastPage`
      // would be inferred as the operation's `TQueryFnData` and collapse the
      // page type to the helper's structural one.
      getNextPageParam: (lastPage, allPages) =>
        nextArchivePage(lastPage, allPages),
    },
  );

  return (
    <PostArchive
      query={query}
      basePath="/study-helper-news"
      emptyTestId="study-helper-news-empty"
      // This sidebar has no search widget.
      sidebar={
        <ArchiveSidebar
          sidebar="study-helper-news-sidebar"
          language={language}
          categoryTo="/study-helper-news/category/$slug"
        />
      }
    />
  );
}

export const Route = createFileRoute('/_app/study-helper-news/')({
  component: StudyHelperNewsArchive,
});
